// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Utility function for navigating to the assets page with flexible filter combinations.
 * Centralizes all navigation logic used across the inventory overview components.
 */
import { AssetSearchListing } from '@/asset/hooks/use-asset-filters';
import { AssetFunctionalStatus } from '@/constants/asset-functional-status';
import { AssetTransitStatus } from '@/constants/asset-transit-status';
import { useNavigate } from '@tanstack/react-router';

type NavigateFunction = ReturnType<typeof useNavigate>;

/**
 * Parameters for asset navigation - all optional to support any combination
 */
interface AssetNavigationParams {
  /** Product IDs to filter by */
  productIds?: string[];
  /** Location IDs to filter by */
  locationIds?: string[];
  /** Functional status to filter by */
  functionalStatus?: AssetFunctionalStatus;
  /** Inventory status to filter by */
  inventoryStatus?: ('in_inventory' | 'not_in_inventory')[];
  /** Transit status to filter by */
  transitStatus?: AssetTransitStatus[];
  /** Whether to include scrapped assets */
  showScrappedAssets?: boolean;
  /** Navigation function from useNavigate hook */
  navigate: NavigateFunction;
}

/**
 * Navigate to assets page with the provided filter combination
 */
export async function navigateToAssets(params: AssetNavigationParams) {
  const {
    productIds,
    locationIds,
    functionalStatus,
    inventoryStatus,
    transitStatus,
    showScrappedAssets,
    navigate,
  } = params;

  const searchParams: AssetSearchListing = {};

  if (productIds?.length) {
    searchParams.products = productIds;
  }

  if (locationIds?.length) {
    searchParams.locations = locationIds;
  }

  if (functionalStatus) {
    searchParams.functionalStatuses = [functionalStatus];
  }

  if (inventoryStatus?.length) {
    searchParams.inventoryStatus = inventoryStatus;
  }

  if (transitStatus?.length) {
    searchParams.transitStatus = transitStatus;
  }

  if (showScrappedAssets) {
    searchParams.scrapped = true;
  }

  return navigate({
    to: '/assets',
    search: searchParams,
  });
}
